import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import axiosInstance from "../axiosInstance";
import PaynowVerificationPanel from "./PaynowVerificationPanel";
import FeeAdjustmentsPanel from "./FeeAdjustmentsPanel";

const tabs = [
  { key: "summary", label: "Summary", icon: "bi-speedometer2" },
  { key: "categories", label: "Fee Categories", icon: "bi-tags" },
  { key: "structures", label: "Fee Structures", icon: "bi-list-check" },
  { key: "adjustments", label: "Discounts & Exemptions", icon: "bi-percent" },
  { key: "invoices", label: "Invoices", icon: "bi-receipt" },
  { key: "payments", label: "Payments", icon: "bi-cash-coin" },
  { key: "paynow", label: "Paynow Verification", icon: "bi-phone" },
  { key: "expenses", label: "Expenses", icon: "bi-wallet2" },
  { key: "arrears", label: "Arrears", icon: "bi-exclamation-triangle" },
  { key: "reports", label: "Reports", icon: "bi-graph-up" },
];

const emptyCategory = {
  id: null,
  name: "",
  code: "",
  description: "",
  is_active: true,
};

const emptyStructure = {
  id: null,
  academic_year: "",
  term: "",
  class_group: "",
  name: "",
  status: "DRAFT",
  due_date: "",
  notes: "",
};

const emptyStructureItem = {
  fee_structure: "",
  category: "",
  currency: "",
  description: "",
  amount: "",
  is_optional: false,
  applicability: "STANDARD",
};

const emptyPayment = {
  invoice: "",
  payment_method: "",
  currency: "",
  amount: "",
  payment_date: "",
  reference_number: "",
  notes: "",
};

const emptyExpenseCategory = {
  id: null,
  name: "",
  code: "",
  description: "",
  is_active: true,
};

const emptyExpense = {
  category: "",
  payment_method: "",
  currency: "",
  description: "",
  amount: "",
  expense_date: "",
  paid_to: "",
  reference_number: "",
  notes: "",
};

function FeesBilling() {
  const [activeTab, setActiveTab] = useState("summary");
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState({});
  const [categories, setCategories] = useState([]);
  const [structures, setStructures] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [payments, setPayments] = useState([]);
  const [academicYears, setAcademicYears] = useState([]);
  const [terms, setTerms] = useState([]);
  const [classGroups, setClassGroups] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [expenseCategories, setExpenseCategories] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [arrears, setArrears] = useState([]);
  const [reportSummary, setReportSummary] = useState({});
  const [categoryForm, setCategoryForm] = useState(emptyCategory);
  const [structureForm, setStructureForm] = useState(emptyStructure);
  const [itemForm, setItemForm] = useState(emptyStructureItem);
  const [paymentForm, setPaymentForm] = useState(emptyPayment);
  const [expenseCategoryForm, setExpenseCategoryForm] = useState(emptyExpenseCategory);
  const [expenseForm, setExpenseForm] = useState(emptyExpense);
  const [searchTerm, setSearchTerm] = useState("");
  const [receiptData, setReceiptData] = useState(null);
  const [statementData, setStatementData] = useState(null);

  const activeCurrencies = useMemo(
    () => currencies.filter((currency) => currency.is_active),
    [currencies]
  );

  const activeCategories = useMemo(
    () => categories.filter((category) => category.is_active),
    [categories]
  );

  const filteredInvoices = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return invoices;

    return invoices.filter((invoice) => {
      return (
        invoice.invoice_number?.toLowerCase().includes(term) ||
        invoice.student_name?.toLowerCase().includes(term) ||
        invoice.student_number?.toLowerCase().includes(term) ||
        invoice.class_group_name?.toLowerCase().includes(term)
      );
    });
  }, [invoices, searchTerm]);

  const loadAll = async () => {
    try {
      setLoading(true);
      await Promise.all([
        axiosInstance.post("fees/bootstrap/"),
        axiosInstance.post("settings/school-finance/bootstrap/"),
      ]);

      const [
        summaryResponse,
        categoriesResponse,
        structuresResponse,
        invoicesResponse,
        paymentsResponse,
        yearsResponse,
        termsResponse,
        groupsResponse,
        currenciesResponse,
        methodsResponse,
        expenseCategoriesResponse,
        expensesResponse,
        arrearsResponse,
        reportsResponse,
      ] = await Promise.all([
        axiosInstance.get("fees/summary/"),
        axiosInstance.get("fees/categories/"),
        axiosInstance.get("fees/structures/"),
        axiosInstance.get("fees/invoices/"),
        axiosInstance.get("fees/payments/"),
        axiosInstance.get("academic/years/"),
        axiosInstance.get("academic/terms/"),
        axiosInstance.get("academic/class-groups/"),
        axiosInstance.get("settings/currencies/"),
        axiosInstance.get("settings/payment-methods/"),
        axiosInstance.get("fees/expense-categories/"),
        axiosInstance.get("fees/expenses/"),
        axiosInstance.get("fees/arrears/"),
        axiosInstance.get("fees/reports/summary/"),
      ]);

      setSummary(summaryResponse.data || {});
      setCategories(categoriesResponse.data || []);
      setStructures(structuresResponse.data || []);
      setInvoices(invoicesResponse.data || []);
      setPayments(paymentsResponse.data || []);
      setAcademicYears(yearsResponse.data || []);
      setTerms(termsResponse.data || []);
      setClassGroups(groupsResponse.data || []);
      setCurrencies(currenciesResponse.data || []);
      setPaymentMethods(methodsResponse.data || []);
      setExpenseCategories(expenseCategoriesResponse.data || []);
      setExpenses(expensesResponse.data || []);
      setArrears(arrearsResponse.data || []);
      setReportSummary(reportsResponse.data || {});
    } catch (error) {
      console.error(error);
      toast.error("Failed to load fees and billing.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
  }, []);

  const saveCategory = async (event) => {
    event.preventDefault();

    try {
      setLoading(true);
      if (categoryForm.id) {
        await axiosInstance.patch(`fees/categories/${categoryForm.id}/`, categoryForm);
        toast.success("Fee category updated.");
      } else {
        await axiosInstance.post("fees/categories/", categoryForm);
        toast.success("Fee category added.");
      }
      setCategoryForm(emptyCategory);
      await loadAll();
    } catch (error) {
      const data = error.response?.data;
      toast.error(data?.detail || Object.values(data || {})?.flat?.()?.[0] || "Failed to save fee category.");
    } finally {
      setLoading(false);
    }
  };

  const saveStructure = async (event) => {
    event.preventDefault();

    try {
      setLoading(true);
      const payload = {
        ...structureForm,
        term: structureForm.term || null,
        due_date: structureForm.due_date || null,
      };

      if (structureForm.id) {
        await axiosInstance.patch(`fees/structures/${structureForm.id}/`, payload);
        toast.success("Fee structure updated.");
      } else {
        await axiosInstance.post("fees/structures/", payload);
        toast.success("Fee structure added.");
      }
      setStructureForm(emptyStructure);
      await loadAll();
    } catch (error) {
      const data = error.response?.data;
      toast.error(data?.detail || Object.values(data || {})?.flat?.()?.[0] || "Failed to save fee structure.");
    } finally {
      setLoading(false);
    }
  };

  const saveStructureItem = async (event) => {
    event.preventDefault();

    try {
      setLoading(true);
      await axiosInstance.post("fees/structure-items/", itemForm);
      toast.success("Fee item added.");
      setItemForm(emptyStructureItem);
      await loadAll();
    } catch (error) {
      const data = error.response?.data;
      toast.error(data?.detail || Object.values(data || {})?.flat?.()?.[0] || "Failed to add fee item.");
    } finally {
      setLoading(false);
    }
  };

  const generateInvoices = async (structure) => {
    try {
      setLoading(true);
      const previewResponse = await axiosInstance.post("fees/preview-invoices/", { fee_structure: structure.id });
      const preview = previewResponse.data || {};
      const summary = preview.summary || {};
      const code = preview.currency_code || "";
      const confirmed = window.confirm(
        `Invoice preview for ${structure.name}\n\n` +
        `Students: ${summary.students || 0}\n` +
        `New invoices: ${summary.new_invoices || 0}\n` +
        `Existing invoices skipped: ${summary.existing_invoices || 0}\n` +
        `Gross: ${code} ${summary.gross_total || 0}\n` +
        `Discounts / exemptions: ${code} ${summary.discount_total || 0}\n` +
        `Net expected: ${code} ${summary.net_total || 0}\n\n` +
        `Generate these invoices now?`
      );
      if (!confirmed) return;
      const response = await axiosInstance.post("fees/generate-invoices/", { fee_structure: structure.id });
      toast.success(`${response.data?.created || 0} invoice(s) generated. ${response.data?.skipped || 0} skipped.`);
      await loadAll();
      setActiveTab("invoices");
    } catch (error) {
      const data = error.response?.data;
      toast.error(data?.detail || "Failed to preview or generate invoices.");
    } finally {
      setLoading(false);
    }
  };

  const loadGrangeFeeSetup = async () => {
    if (!window.confirm("Load The Grange fee defaults (USD/ZiG, Cash/Swipe/RTGS, standard fees and draft class fee structures)? Existing invoices and payments will not be changed.")) return;
    try {
      setLoading(true);
      const response = await axiosInstance.post("fees/grange-setup/");
      toast.success(response.data?.message || "The Grange fee defaults loaded.");
      await loadAll();
      setActiveTab("structures");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to load The Grange fee setup.");
    } finally {
      setLoading(false);
    }
  };

  const recordPayment = async (event) => {
    event.preventDefault();

    try {
      setLoading(true);
      await axiosInstance.post("fees/payments/", paymentForm);
      toast.success("Payment recorded successfully.");
      setPaymentForm(emptyPayment);
      await loadAll();
    } catch (error) {
      const data = error.response?.data;
      toast.error(data?.detail || Object.values(data || {})?.flat?.()?.[0] || "Failed to record payment.");
    } finally {
      setLoading(false);
    }
  };

  const saveExpenseCategory = async (event) => {
    event.preventDefault();

    try {
      setLoading(true);
      if (expenseCategoryForm.id) {
        await axiosInstance.patch(`fees/expense-categories/${expenseCategoryForm.id}/`, expenseCategoryForm);
        toast.success("Expense category updated.");
      } else {
        await axiosInstance.post("fees/expense-categories/", expenseCategoryForm);
        toast.success("Expense category added.");
      }
      setExpenseCategoryForm(emptyExpenseCategory);
      await loadAll();
    } catch (error) {
      const data = error.response?.data;
      toast.error(data?.detail || Object.values(data || {})?.flat?.()?.[0] || "Failed to save expense category.");
    } finally {
      setLoading(false);
    }
  };

  const saveExpense = async (event) => {
    event.preventDefault();

    try {
      setLoading(true);
      const payload = {
        ...expenseForm,
        payment_method: expenseForm.payment_method || null,
      };
      if (!payload.expense_date) {
        delete payload.expense_date;
      }

      await axiosInstance.post("fees/expenses/", payload);
      toast.success("Expense recorded successfully.");
      setExpenseForm(emptyExpense);
      await loadAll();
    } catch (error) {
      const data = error.response?.data;
      toast.error(data?.detail || Object.values(data || {})?.flat?.()?.[0] || "Failed to record expense.");
    } finally {
      setLoading(false);
    }
  };

  const editCategory = (category) => {
    setCategoryForm(category);
    setActiveTab("categories");
  };

  const editStructure = (structure) => {
    setStructureForm({
      id: structure.id,
      academic_year: structure.academic_year,
      term: structure.term || "",
      class_group: structure.class_group,
      name: structure.name,
      status: structure.status,
      due_date: structure.due_date || "",
      notes: structure.notes || "",
    });
    setActiveTab("structures");
  };

  const selectInvoiceForPayment = (invoice) => {
    setPaymentForm((prev) => ({
      ...prev,
      invoice: invoice.id,
      currency: invoice.currency,
      amount: invoice.balance,
    }));
    setActiveTab("payments");
  };

  const openReceipt = async (payment) => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`fees/payments/${payment.id}/receipt/`);
      setReceiptData(response.data);
    } catch (error) {
      console.error(error);
      toast.error("Failed to load receipt.");
    } finally {
      setLoading(false);
    }
  };

  const openStatement = async (studentId) => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`fees/students/${studentId}/statement/`);
      setStatementData(response.data);
    } catch (error) {
      console.error(error);
      toast.error("Failed to load fee statement.");
    } finally {
      setLoading(false);
    }
  };

  const printReceipt = (data) => {
    const school = data.school || {};
    const receipt = data.receipt || {};
    const invoice = data.invoice || {};
    const student = data.student || {};

    const printWindow = window.open("", "_blank", "width=900,height=1000");
    if (!printWindow) {
      toast.error("Please allow pop-ups to print the receipt.");
      return;
    }

    const escapeHtml = (value) =>
      String(value || "")
        .replaceAll("&", "&amp;")
        .replaceAll("<", "&lt;")
        .replaceAll(">", "&gt;")
        .replaceAll('"', "&quot;")
        .replaceAll("'", "&#039;");

    printWindow.document.write(`
      <!doctype html>
      <html>
        <head>
          <title>Receipt ${escapeHtml(receipt.receipt_number)}</title>
          <style>
            * {
              box-sizing: border-box;
            }

            body {
              margin: 0;
              padding: 24px;
              color: #111827;
              font-family: Arial, Helvetica, sans-serif;
              background: #ffffff;
            }

            .receipt {
              width: 100%;
              max-width: 760px;
              margin: 0 auto;
              border: 1px solid #d1d5db;
              padding: 28px;
            }

            .header {
              display: flex;
              justify-content: space-between;
              gap: 20px;
              border-bottom: 2px solid #125b2d;
              padding-bottom: 18px;
              margin-bottom: 20px;
            }

            .school {
              display: flex;
              align-items: center;
              gap: 16px;
            }

            .logo {
              width: 86px;
              height: 86px;
              object-fit: contain;
            }

            h1, h2, h3, p {
              margin: 0;
            }

            h1 {
              color: #125b2d;
              font-size: 24px;
              margin-bottom: 4px;
            }

            .muted {
              color: #6b7280;
              font-size: 12px;
              line-height: 1.5;
            }

            .receipt-title {
              text-align: right;
            }

            .receipt-title .label {
              color: #6b7280;
              font-size: 12px;
              font-weight: 700;
              letter-spacing: 0.08em;
              text-transform: uppercase;
            }

            .receipt-title .number {
              font-size: 22px;
              font-weight: 800;
              margin-top: 4px;
            }

            .grid {
              display: grid;
              grid-template-columns: repeat(3, 1fr);
              gap: 12px;
              margin-bottom: 20px;
            }

            .box {
              border: 1px solid #e5e7eb;
              padding: 12px;
              min-height: 68px;
            }

            .box .label {
              color: #6b7280;
              font-size: 10px;
              font-weight: 700;
              text-transform: uppercase;
              margin-bottom: 6px;
            }

            .box .value {
              font-size: 14px;
              font-weight: 700;
            }

            table {
              width: 100%;
              border-collapse: collapse;
              margin-top: 8px;
            }

            th, td {
              border: 1px solid #d1d5db;
              padding: 12px;
              font-size: 14px;
            }

            th {
              text-align: left;
              width: 45%;
              background: #f9fafb;
            }

            td {
              text-align: right;
            }

            .strong {
              font-weight: 800;
            }

            .footer-row {
              display: flex;
              justify-content: space-between;
              border-top: 1px solid #e5e7eb;
              margin-top: 24px;
              padding-top: 18px;
            }

            .footer-note {
              color: #6b7280;
              text-align: center;
              font-size: 12px;
              margin-top: 28px;
            }

            @media print {
              body {
                padding: 0;
              }

              .receipt {
                border: 0;
                max-width: none;
                padding: 10mm;
              }
            }
          </style>
        </head>
        <body>
          <main class="receipt">
            <section class="header">
              <div class="school">
                ${
                  school.logo_url
                    ? `<img class="logo" src="${escapeHtml(school.logo_url)}" alt="School logo" />`
                    : ""
                }
                <div>
                  <h1>${escapeHtml(school.name || "School")}</h1>
                  <p class="muted">${escapeHtml(school.motto)}</p>
                  <p class="muted">${escapeHtml(school.address)}</p>
                  <p class="muted">${escapeHtml([school.phone, school.email, school.website].filter(Boolean).join(" | "))}</p>
                </div>
              </div>
              <div class="receipt-title">
                <div class="label">Payment Receipt</div>
                <div class="number">${escapeHtml(receipt.receipt_number)}</div>
              </div>
            </section>

            <section class="grid">
              <div class="box"><div class="label">Student</div><div class="value">${escapeHtml(student.name)}</div></div>
              <div class="box"><div class="label">Student Number</div><div class="value">${escapeHtml(student.student_number)}</div></div>
              <div class="box"><div class="label">Class Group</div><div class="value">${escapeHtml(student.class_group)}</div></div>
              <div class="box"><div class="label">Invoice</div><div class="value">${escapeHtml(invoice.invoice_number)}</div></div>
              <div class="box"><div class="label">Payment Date</div><div class="value">${escapeHtml(receipt.payment_date)}</div></div>
              <div class="box"><div class="label">Payment Method</div><div class="value">${escapeHtml(receipt.payment_method_name)}</div></div>
            </section>

            <table>
              <tbody>
                <tr><th>Amount Paid</th><td class="strong">${escapeHtml(receipt.currency_code)} ${escapeHtml(receipt.amount)}</td></tr>
                <tr><th>Reference Number</th><td>${escapeHtml(receipt.reference_number || "-")}</td></tr>
                <tr><th>Invoice Total</th><td>${escapeHtml(invoice.currency_code)} ${escapeHtml(invoice.total_amount)}</td></tr>
                <tr><th>Total Paid</th><td>${escapeHtml(invoice.currency_code)} ${escapeHtml(invoice.amount_paid)}</td></tr>
                <tr><th>Balance</th><td class="strong">${escapeHtml(invoice.currency_code)} ${escapeHtml(invoice.balance)}</td></tr>
              </tbody>
            </table>

            <section class="footer-row">
              <div>
                <div class="muted">Received By</div>
                <div class="strong">${escapeHtml(receipt.received_by_name || "Admin")}</div>
              </div>
              <div>
                <div class="muted">Status</div>
                <div class="strong">${escapeHtml(receipt.status)}</div>
              </div>
            </section>

            ${school.receipt_footer ? `<div class="footer-note">${escapeHtml(school.receipt_footer)}</div>` : ""}
          </main>
          <script>
            window.onload = function () {
              window.focus();
              window.print();
            };
          </script>
        </body>
      </html>
    `);

    printWindow.document.close();
  };

  return (
    <div>
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-4">
        <div>
          <h3 className="fw-bold mb-1" style={{ color: "#125b2d" }}>
            Fees & Billing
          </h3>
          <p className="text-muted mb-0">
            Manage fee structures, generate student invoices, record payments, and track balances.
          </p>
        </div>
        <button className="btn btn-light border" onClick={loadAll} disabled={loading}>
          <i className="bi bi-arrow-clockwise me-2"></i>
          Refresh
        </button>
      </div>

      <div className="row g-3 mb-4">
        <SummaryCard label="Invoices" value={summary.invoices || 0} icon="bi-receipt" />
        <SummaryCard label="Paid Invoices" value={summary.paid_invoices || 0} icon="bi-check2-circle" />
        <SummaryCard label="Outstanding" value={summary.outstanding_balance || 0} icon="bi-exclamation-circle" />
        <SummaryCard label="Payments" value={summary.payments_received || 0} icon="bi-cash-coin" />
        <SummaryCard label="Expenses" value={reportSummary?.counts?.expenses || 0} icon="bi-wallet2" />
        <SummaryCard label="Arrears" value={reportSummary?.counts?.arrears_invoices || 0} icon="bi-exclamation-triangle" />
      </div>

      <div className="d-flex flex-wrap gap-2 mb-3">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            type="button"
            className={`btn btn-sm ${activeTab === tab.key ? "btn-success" : "btn-light border"}`}
            onClick={() => setActiveTab(tab.key)}
          >
            <i className={`bi ${tab.icon} me-2`}></i>
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === "summary" && (
        <SetupCard title="Finance Flow">
          <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
            <div className="text-muted small">The Grange defaults: Tuition USD 1,300 + Development Levy USD 100; Desk Levy USD 500 for new students; Application Fee USD 50 manual.</div>
            <button type="button" className="btn btn-success" onClick={loadGrangeFeeSetup} disabled={loading}>
              <i className="bi bi-magic me-2"></i>Load The Grange Fee Setup
            </button>
          </div>
          <div className="row g-3">
            <InfoBox title="1. Configure Categories" text="Create tuition, exam, transport, uniform, and other billing categories." />
            <InfoBox title="2. Build Fee Structure" text="Choose academic year, term, class group, then add fee items and amounts." />
            <InfoBox title="3. Generate Invoices" text="The system creates invoices for students in that class group and skips existing invoices." />
            <InfoBox title="4. Record Payments" text="Payments create receipt numbers and update invoice paid amount and balance." />
          </div>
        </SetupCard>
      )}

      {activeTab === "categories" && (
        <SetupCard title="Fee Categories">
          <form className="row g-3 mb-4" onSubmit={saveCategory}>
            <TextInput label="Name" value={categoryForm.name} onChange={(value) => setCategoryForm((prev) => ({ ...prev, name: value }))} required />
            <TextInput label="Code" value={categoryForm.code} onChange={(value) => setCategoryForm((prev) => ({ ...prev, code: value.toUpperCase() }))} required />
            <TextInput label="Description" value={categoryForm.description || ""} onChange={(value) => setCategoryForm((prev) => ({ ...prev, description: value }))} />
            <SwitchInput label="Active" checked={categoryForm.is_active} onChange={(value) => setCategoryForm((prev) => ({ ...prev, is_active: value }))} />
            <SaveBar loading={loading} label={categoryForm.id ? "Update Category" : "Add Category"} onCancel={categoryForm.id ? () => setCategoryForm(emptyCategory) : null} />
          </form>

          <SimpleTable
            headers={["Name", "Code", "Status", "Actions"]}
            rows={categories.map((category) => [
              category.name,
              category.code,
              <Status value={category.is_active} />,
              <button className="btn btn-sm btn-light border" onClick={() => editCategory(category)}><i className="bi bi-pencil-square text-success"></i></button>,
            ])}
          />
        </SetupCard>
      )}

      {activeTab === "structures" && (
        <SetupCard title="Fee Structures">
          <form className="row g-3 mb-4" onSubmit={saveStructure}>
            <SelectInput label="Academic Year" value={structureForm.academic_year} onChange={(value) => setStructureForm((prev) => ({ ...prev, academic_year: value }))} options={academicYears.map((item) => [item.id, item.name])} required />
            <SelectInput label="Term" value={structureForm.term} onChange={(value) => setStructureForm((prev) => ({ ...prev, term: value }))} options={terms.map((item) => [item.id, `${item.academic_year_name} - ${item.name}`])} />
            <SelectInput label="Class Group" value={structureForm.class_group} onChange={(value) => setStructureForm((prev) => ({ ...prev, class_group: value }))} options={classGroups.map((item) => [item.id, `${item.academic_year_name} - ${item.display_name}`])} required />
            <TextInput label="Structure Name" value={structureForm.name} onChange={(value) => setStructureForm((prev) => ({ ...prev, name: value }))} placeholder="Term 1 Fees" required />
            <SelectInput label="Status" value={structureForm.status} onChange={(value) => setStructureForm((prev) => ({ ...prev, status: value }))} options={[["DRAFT", "Draft"], ["ACTIVE", "Active"], ["ARCHIVED", "Archived"]]} required />
            <TextInput type="date" label="Due Date" value={structureForm.due_date || ""} onChange={(value) => setStructureForm((prev) => ({ ...prev, due_date: value }))} />
            <SaveBar loading={loading} label={structureForm.id ? "Update Structure" : "Add Structure"} onCancel={structureForm.id ? () => setStructureForm(emptyStructure) : null} />
          </form>

          <form className="row g-3 mb-4 border-top pt-3" onSubmit={saveStructureItem}>
            <SelectInput label="Fee Structure" value={itemForm.fee_structure} onChange={(value) => setItemForm((prev) => ({ ...prev, fee_structure: value }))} options={structures.map((item) => [item.id, `${item.name} - ${item.class_group_name}`])} required />
            <SelectInput label="Category" value={itemForm.category} onChange={(value) => setItemForm((prev) => ({ ...prev, category: value }))} options={activeCategories.map((item) => [item.id, item.name])} required />
            <SelectInput label="Currency" value={itemForm.currency} onChange={(value) => setItemForm((prev) => ({ ...prev, currency: value }))} options={activeCurrencies.map((item) => [item.id, `${item.code} - ${item.name}`])} required />
            <TextInput label="Description" value={itemForm.description} onChange={(value) => setItemForm((prev) => ({ ...prev, description: value }))} />
            <TextInput type="number" label="Amount" value={itemForm.amount} onChange={(value) => setItemForm((prev) => ({ ...prev, amount: value }))} required />
            <SelectInput label="Applicability" value={itemForm.applicability} onChange={(value) => setItemForm((prev) => ({ ...prev, applicability: value }))} options={[["STANDARD", "All students"], ["NEW_STUDENT", "New students only"], ["MANUAL", "Manual / individual only"]]} required />
            <SaveBar loading={loading} label="Add Fee Item" />
          </form>

          <SimpleTable
            headers={["Structure", "Class", "Status", "Items", "Standard Total", "Actions"]}
            rows={structures.map((structure) => [
              structure.name,
              structure.class_group_name,
              <StatusText value={structure.status} />,
              structure.items?.length || 0,
              (structure.items || [])
                .filter((item) => (item.applicability || "STANDARD") === "STANDARD")
                .reduce((sum, item) => sum + Number(item.amount || 0), 0)
                .toFixed(2),
              <div className="d-flex gap-1">
                <button className="btn btn-sm btn-light border" onClick={() => editStructure(structure)} title="Edit"><i className="bi bi-pencil-square text-success"></i></button>
                <button className="btn btn-sm btn-success" onClick={() => generateInvoices(structure)} disabled={structure.status !== "ACTIVE"} title="Generate invoices"><i className="bi bi-receipt-cutoff"></i></button>
              </div>,
            ])}
          />
        </SetupCard>
      )}

      {activeTab === "adjustments" && (
        <FeeAdjustmentsPanel
          academicYears={academicYears}
          terms={terms}
          currencies={currencies}
          onChanged={loadAll}
        />
      )}

      {activeTab === "invoices" && (
        <SetupCard title="Student Invoices">
          <div className="d-flex justify-content-end mb-3">
            <input className="form-control" style={{ maxWidth: 360 }} placeholder="Search invoice, student, class..." value={searchTerm} onChange={(event) => setSearchTerm(event.target.value)} />
          </div>
          <SimpleTable
            headers={["Invoice", "Student", "Class", "Gross", "Discount", "Total", "Paid", "Balance", "Status", "Actions"]}
            rows={filteredInvoices.map((invoice) => [
              invoice.invoice_number,
              <div><div className="fw-semibold">{invoice.student_name}</div><small className="text-muted">{invoice.student_number}</small></div>,
              invoice.class_group_name,
              `${invoice.currency_code} ${invoice.subtotal}`,
              `${invoice.currency_code} ${invoice.discount_amount}`,
              `${invoice.currency_code} ${invoice.total_amount}`,
              invoice.amount_paid,
              invoice.balance,
              <StatusText value={invoice.status} />,
              <div className="d-flex gap-1">
                <button className="btn btn-sm btn-success" disabled={Number(invoice.balance) <= 0} onClick={() => selectInvoiceForPayment(invoice)}>Pay</button>
                <button className="btn btn-sm btn-light border" onClick={() => openStatement(invoice.student)}>Statement</button>
              </div>,
            ])}
          />
        </SetupCard>
      )}

      {activeTab === "payments" && (
        <SetupCard title="Record Payment">
          <form className="row g-3 mb-4" onSubmit={recordPayment}>
            <SelectInput label="Invoice" value={paymentForm.invoice} onChange={(value) => {
              const invoice = invoices.find((item) => String(item.id) === String(value));
              setPaymentForm((prev) => ({ ...prev, invoice: value, currency: invoice?.currency || prev.currency, amount: invoice?.balance || prev.amount }));
            }} options={invoices.filter((item) => Number(item.balance) > 0).map((item) => [item.id, `${item.invoice_number} - ${item.student_name} (${item.currency_code} ${item.balance})`])} required />
            <SelectInput label="Payment Method" value={paymentForm.payment_method} onChange={(value) => setPaymentForm((prev) => ({ ...prev, payment_method: value }))} options={paymentMethods.filter((item) => item.is_active).map((item) => [item.id, item.display_name || item.name])} required />
            <SelectInput label="Currency" value={paymentForm.currency} onChange={(value) => setPaymentForm((prev) => ({ ...prev, currency: value }))} options={activeCurrencies.map((item) => [item.id, `${item.code} - ${item.name}`])} required />
            <TextInput type="number" label="Amount" value={paymentForm.amount} onChange={(value) => setPaymentForm((prev) => ({ ...prev, amount: value }))} required />
            <TextInput type="date" label="Payment Date" value={paymentForm.payment_date} onChange={(value) => setPaymentForm((prev) => ({ ...prev, payment_date: value }))} />
            <TextInput label="Reference" value={paymentForm.reference_number} onChange={(value) => setPaymentForm((prev) => ({ ...prev, reference_number: value }))} />
            <SaveBar loading={loading} label="Record Payment" />
          </form>

          <SimpleTable
            headers={["Receipt", "Student", "Method", "Amount", "Date", "Status", "Actions"]}
            rows={payments.map((payment) => [
              payment.receipt_number,
              payment.student_name,
              payment.payment_method_name,
              `${payment.currency_code} ${payment.amount}`,
              payment.payment_date,
              <StatusText value={payment.status} />,
              <button className="btn btn-sm btn-light border" onClick={() => openReceipt(payment)}>
                <i className="bi bi-printer text-success me-1"></i>
                Receipt
              </button>,
            ])}
          />
        </SetupCard>
      )}

      {activeTab === "paynow" && (
        <PaynowVerificationPanel onChanged={loadAll} />
      )}

      {activeTab === "expenses" && (
        <SetupCard title="Expenses">
          <form className="row g-3 mb-4" onSubmit={saveExpenseCategory}>
            <TextInput label="Category Name" value={expenseCategoryForm.name} onChange={(value) => setExpenseCategoryForm((prev) => ({ ...prev, name: value }))} required />
            <TextInput label="Code" value={expenseCategoryForm.code} onChange={(value) => setExpenseCategoryForm((prev) => ({ ...prev, code: value.toUpperCase() }))} required />
            <TextInput label="Description" value={expenseCategoryForm.description || ""} onChange={(value) => setExpenseCategoryForm((prev) => ({ ...prev, description: value }))} />
            <SwitchInput label="Active" checked={expenseCategoryForm.is_active} onChange={(value) => setExpenseCategoryForm((prev) => ({ ...prev, is_active: value }))} />
            <SaveBar loading={loading} label={expenseCategoryForm.id ? "Update Expense Category" : "Add Expense Category"} onCancel={expenseCategoryForm.id ? () => setExpenseCategoryForm(emptyExpenseCategory) : null} />
          </form>

          <form className="row g-3 mb-4 border-top pt-3" onSubmit={saveExpense}>
            <SelectInput label="Category" value={expenseForm.category} onChange={(value) => setExpenseForm((prev) => ({ ...prev, category: value }))} options={expenseCategories.filter((item) => item.is_active).map((item) => [item.id, item.name])} required />
            <SelectInput label="Payment Method" value={expenseForm.payment_method} onChange={(value) => setExpenseForm((prev) => ({ ...prev, payment_method: value }))} options={paymentMethods.filter((item) => item.is_active).map((item) => [item.id, item.display_name || item.name])} />
            <SelectInput label="Currency" value={expenseForm.currency} onChange={(value) => setExpenseForm((prev) => ({ ...prev, currency: value }))} options={activeCurrencies.map((item) => [item.id, `${item.code} - ${item.name}`])} required />
            <TextInput label="Description" value={expenseForm.description} onChange={(value) => setExpenseForm((prev) => ({ ...prev, description: value }))} required />
            <TextInput type="number" label="Amount" value={expenseForm.amount} onChange={(value) => setExpenseForm((prev) => ({ ...prev, amount: value }))} required />
            <TextInput type="date" label="Expense Date" value={expenseForm.expense_date} onChange={(value) => setExpenseForm((prev) => ({ ...prev, expense_date: value }))} />
            <TextInput label="Paid To" value={expenseForm.paid_to} onChange={(value) => setExpenseForm((prev) => ({ ...prev, paid_to: value }))} />
            <TextInput label="Reference" value={expenseForm.reference_number} onChange={(value) => setExpenseForm((prev) => ({ ...prev, reference_number: value }))} />
            <SaveBar loading={loading} label="Record Expense" />
          </form>

          <SimpleTable
            headers={["Date", "Category", "Description", "Paid To", "Method", "Amount", "Recorded By"]}
            rows={expenses.map((expense) => [
              expense.expense_date,
              expense.category_name,
              expense.description,
              expense.paid_to || "-",
              expense.payment_method_name || "-",
              `${expense.currency_code} ${expense.amount}`,
              expense.recorded_by_name || "-",
            ])}
          />
        </SetupCard>
      )}

      {activeTab === "arrears" && (
        <SetupCard title="Arrears Tracking">
          <SimpleTable
            headers={["Student", "Number", "Class", "Invoice", "Total", "Paid", "Balance", "Status", "Actions"]}
            rows={arrears.map((invoice) => [
              invoice.student_name,
              invoice.student_number,
              invoice.class_group_name,
              invoice.invoice_number,
              `${invoice.currency_code} ${invoice.total_amount}`,
              `${invoice.currency_code} ${invoice.amount_paid}`,
              <strong>{invoice.currency_code} {invoice.balance}</strong>,
              <StatusText value={invoice.status} />,
              <div className="d-flex gap-1">
                <button className="btn btn-sm btn-success" onClick={() => selectInvoiceForPayment(invoice)}>Pay</button>
                <button className="btn btn-sm btn-light border" onClick={() => openStatement(invoice.student)}>Statement</button>
              </div>,
            ])}
          />
        </SetupCard>
      )}

      {activeTab === "reports" && (
        <SetupCard title="Multi-Currency Finance Reports">
          <div className="row g-3 mb-4">
            <ReportBlock title="Income Received" data={reportSummary.income} />
            <ReportBlock title="Expenses" data={reportSummary.expenses} />
            <ReportBlock title="Invoices Billed" data={reportSummary.billed} />
            <ReportBlock title="Arrears" data={reportSummary.arrears} />
          </div>

          <div className="alert alert-light border mb-0">
            <strong>Net default-currency equivalent:</strong>{" "}
            {reportSummary.default_currency || "Default"} {reportSummary.net_default_equivalent || 0}
            <div className="text-muted small mt-1">
              Native currency totals are shown separately. Default equivalent uses the exchange rate configured in Settings.
            </div>
          </div>
        </SetupCard>
      )}

      {receiptData && (
        <ReceiptModal
          data={receiptData}
          onClose={() => setReceiptData(null)}
          onPrint={() => printReceipt(receiptData)}
        />
      )}

      {statementData && (
        <StatementModal
          data={statementData}
          onClose={() => setStatementData(null)}
        />
      )}
    </div>
  );
}

function SummaryCard({ label, value, icon }) {
  return (
    <div className="col-md-3">
      <div className="card border-0 shadow-sm h-100">
        <div className="card-body d-flex justify-content-between align-items-center">
          <div>
            <small className="text-muted fw-bold">{label}</small>
            <h4 className="fw-bold mb-0">{value}</h4>
          </div>
          <div className="rounded-3 p-3 text-success" style={{ background: "#eef8f1" }}>
            <i className={`bi ${icon} fs-4`}></i>
          </div>
        </div>
      </div>
    </div>
  );
}

function SetupCard({ title, children }) {
  return (
    <div className="card border-0 shadow-sm">
      <div className="card-header bg-white">
        <h5 className="mb-0 fw-bold">{title}</h5>
      </div>
      <div className="card-body">{children}</div>
    </div>
  );
}

function InfoBox({ title, text }) {
  return (
    <div className="col-md-3">
      <div className="border rounded-3 p-3 h-100 bg-light">
        <h6 className="fw-bold">{title}</h6>
        <p className="text-muted mb-0 small">{text}</p>
      </div>
    </div>
  );
}

function TextInput({ label, value, onChange, type = "text", placeholder = "", required = false }) {
  return (
    <div className="col-md-4">
      <label className="form-label fw-semibold">{label}</label>
      <input type={type} className="form-control" value={value || ""} placeholder={placeholder} onChange={(event) => onChange(event.target.value)} required={required} />
    </div>
  );
}

function SelectInput({ label, value, onChange, options, required = false }) {
  return (
    <div className="col-md-4">
      <label className="form-label fw-semibold">{label}</label>
      <select className="form-select" value={value || ""} onChange={(event) => onChange(event.target.value)} required={required}>
        <option value="">Select {label.toLowerCase()}</option>
        {options.map(([optionValue, optionLabel]) => (
          <option key={optionValue} value={optionValue}>{optionLabel}</option>
        ))}
      </select>
    </div>
  );
}

function SwitchInput({ label, checked, onChange }) {
  return (
    <div className="col-md-4 d-flex align-items-end">
      <label className="form-check form-switch mb-2">
        <input type="checkbox" className="form-check-input" checked={Boolean(checked)} onChange={(event) => onChange(event.target.checked)} />
        <span className="form-check-label fw-semibold">{label}</span>
      </label>
    </div>
  );
}

function SaveBar({ loading, label, onCancel }) {
  return (
    <div className="col-12 d-flex gap-2">
      <button className="btn btn-success" disabled={loading}>{loading ? "Saving..." : label}</button>
      {onCancel && <button type="button" className="btn btn-light border" onClick={onCancel}>Cancel Edit</button>}
    </div>
  );
}

function Status({ value }) {
  return <span className={`badge ${value ? "text-bg-success" : "text-bg-secondary"}`}>{value ? "Active" : "Inactive"}</span>;
}

function StatusText({ value }) {
  const normalized = String(value || "").replaceAll("_", " ");
  const isGood = ["ACTIVE", "PAID", "CONFIRMED", "ISSUED"].includes(value);
  const isWarn = ["DRAFT", "PARTIALLY_PAID", "PENDING", "OVERDUE"].includes(value);
  return <span className={`badge ${isGood ? "text-bg-success" : isWarn ? "text-bg-warning" : "text-bg-secondary"}`}>{normalized}</span>;
}

function SimpleTable({ headers, rows }) {
  return (
    <div className="table-responsive">
      <table className="table table-hover align-middle mb-0">
        <thead className="table-light">
          <tr>{headers.map((header) => <th key={header}>{header}</th>)}</tr>
        </thead>
        <tbody>
          {rows.length ? rows.map((row, rowIndex) => (
            <tr key={rowIndex}>{row.map((cell, cellIndex) => <td key={cellIndex}>{cell}</td>)}</tr>
          )) : (
            <tr><td colSpan={headers.length} className="text-center text-muted py-4">No records yet.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function ReceiptModal({ data, onClose, onPrint }) {
  const school = data.school || {};
  const receipt = data.receipt || {};
  const invoice = data.invoice || {};
  const student = data.student || {};

  return (
    <div className="modal fade show d-block" tabIndex="-1" style={{ background: "rgba(0,0,0,0.45)" }}>
      <div className="modal-dialog modal-lg modal-dialog-scrollable">
        <div className="modal-content border-0 shadow">
          <div className="modal-header">
            <div>
              <h5 className="modal-title fw-bold">Payment Receipt</h5>
              <small className="text-muted">{receipt.receipt_number}</small>
            </div>
            <button type="button" className="btn-close" onClick={onClose}></button>
          </div>

          <div className="modal-body">
            <div className="receipt-print border rounded-3 p-4 bg-white">
              <div className="d-flex align-items-center justify-content-between border-bottom pb-3 mb-3">
                <div className="d-flex align-items-center gap-3">
                  {school.logo_url && (
                    <img
                      src={school.logo_url}
                      alt="School logo"
                      style={{ width: 78, height: 78, objectFit: "contain" }}
                    />
                  )}
                  <div>
                    <h4 className="fw-bold mb-1" style={{ color: "#125b2d" }}>
                      {school.name || "School"}
                    </h4>
                    <div className="text-muted small">{school.motto}</div>
                    <div className="text-muted small">{school.address}</div>
                    <div className="text-muted small">
                      {[school.phone, school.email, school.website].filter(Boolean).join(" | ")}
                    </div>
                  </div>
                </div>
                <div className="text-end">
                  <div className="text-uppercase text-muted fw-bold small">Receipt</div>
                  <h5 className="fw-bold mb-0">{receipt.receipt_number}</h5>
                </div>
              </div>

              <div className="row g-3 mb-3">
                <ReceiptInfo label="Student" value={student.name} />
                <ReceiptInfo label="Student Number" value={student.student_number} />
                <ReceiptInfo label="Class Group" value={student.class_group} />
                <ReceiptInfo label="Invoice" value={invoice.invoice_number} />
                <ReceiptInfo label="Payment Date" value={receipt.payment_date} />
                <ReceiptInfo label="Payment Method" value={receipt.payment_method_name} />
              </div>

              <table className="table table-bordered align-middle">
                <tbody>
                  <tr>
                    <th>Amount Paid</th>
                    <td className="text-end fw-bold">
                      {receipt.currency_code} {receipt.amount}
                    </td>
                  </tr>
                  <tr>
                    <th>Reference Number</th>
                    <td className="text-end">{receipt.reference_number || "-"}</td>
                  </tr>
                  <tr>
                    <th>Invoice Total</th>
                    <td className="text-end">{invoice.currency_code} {invoice.total_amount}</td>
                  </tr>
                  <tr>
                    <th>Total Paid</th>
                    <td className="text-end">{invoice.currency_code} {invoice.amount_paid}</td>
                  </tr>
                  <tr>
                    <th>Balance</th>
                    <td className="text-end fw-bold">{invoice.currency_code} {invoice.balance}</td>
                  </tr>
                </tbody>
              </table>

              <div className="d-flex justify-content-between mt-4 pt-3 border-top">
                <div>
                  <div className="text-muted small">Received By</div>
                  <div className="fw-semibold">{receipt.received_by_name || "Admin"}</div>
                </div>
                <div className="text-end">
                  <div className="text-muted small">Status</div>
                  <div className="fw-semibold">{receipt.status}</div>
                </div>
              </div>

              {school.receipt_footer && (
                <div className="text-center text-muted small mt-4">
                  {school.receipt_footer}
                </div>
              )}
            </div>
          </div>

          <div className="modal-footer">
            <button className="btn btn-light" onClick={onClose}>Close</button>
            <button className="btn btn-success" onClick={onPrint}>
              <i className="bi bi-printer me-2"></i>
              Print Receipt
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ReceiptInfo({ label, value }) {
  return (
    <div className="col-md-4">
      <div className="border rounded-3 p-2 h-100">
        <small className="text-muted fw-bold text-uppercase" style={{ fontSize: 11 }}>
          {label}
        </small>
        <div className="fw-semibold">{value || "-"}</div>
      </div>
    </div>
  );
}

function ReportBlock({ title, data }) {
  const rows = data?.by_currency || [];

  return (
    <div className="col-lg-6">
      <div className="border rounded-3 p-3 h-100">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <h6 className="fw-bold mb-0">{title}</h6>
          <span className="badge text-bg-light border">
            Default Eq: {data?.default_equivalent_total || 0}
          </span>
        </div>
        {rows.length ? (
          <table className="table table-sm align-middle mb-0">
            <thead className="table-light">
              <tr>
                <th>Currency</th>
                <th>Total</th>
                <th>Default Eq.</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.currency_id || row.currency_code}>
                  <td>{row.currency_code}</td>
                  <td>{row.total}</td>
                  <td>{row.default_equivalent}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="text-muted small">No records yet.</div>
        )}
      </div>
    </div>
  );
}

function StatementModal({ data, onClose }) {
  const school = data.school || {};
  const student = data.student || {};
  const summary = data.summary || {};
  const invoices = data.invoices || [];
  const payments = data.payments || [];

  return (
    <div className="modal fade show d-block" tabIndex="-1" style={{ background: "rgba(0,0,0,0.45)" }}>
      <div className="modal-dialog modal-xl modal-dialog-scrollable">
        <div className="modal-content border-0 shadow">
          <div className="modal-header">
            <div>
              <h5 className="modal-title fw-bold">Fee Statement</h5>
              <small className="text-muted">{student.name} - {student.student_number}</small>
            </div>
            <button type="button" className="btn-close" onClick={onClose}></button>
          </div>

          <div className="modal-body">
            <div className="border rounded-3 p-4 bg-white">
              <div className="d-flex justify-content-between border-bottom pb-3 mb-3">
                <div>
                  <h4 className="fw-bold mb-1" style={{ color: "#125b2d" }}>{school.name || "School"}</h4>
                  <div className="text-muted small">{school.address}</div>
                  <div className="text-muted small">{[school.phone, school.email, school.website].filter(Boolean).join(" | ")}</div>
                </div>
                <div className="text-end">
                  <div className="text-uppercase text-muted fw-bold small">Fee Statement</div>
                  <div className="fw-bold">{student.class_group || "No class"}</div>
                </div>
              </div>

              <div className="row g-3 mb-4">
                <ReportBlock title="Billed" data={summary.billed} />
                <ReportBlock title="Paid" data={summary.paid} />
                <ReportBlock title="Balance / Arrears" data={summary.balance} />
              </div>

              <h6 className="fw-bold">Invoices</h6>
              <SimpleTable
                headers={["Invoice", "Term", "Total", "Paid", "Balance", "Status"]}
                rows={invoices.map((invoice) => [
                  invoice.invoice_number,
                  invoice.term_name || "-",
                  `${invoice.currency_code} ${invoice.total_amount}`,
                  `${invoice.currency_code} ${invoice.amount_paid}`,
                  `${invoice.currency_code} ${invoice.balance}`,
                  <StatusText value={invoice.status} />,
                ])}
              />

              <h6 className="fw-bold mt-4">Payments</h6>
              <SimpleTable
                headers={["Receipt", "Date", "Method", "Amount", "Reference"]}
                rows={payments.map((payment) => [
                  payment.receipt_number,
                  payment.payment_date,
                  payment.payment_method_name,
                  `${payment.currency_code} ${payment.amount}`,
                  payment.reference_number || "-",
                ])}
              />
            </div>
          </div>

          <div className="modal-footer">
            <button className="btn btn-light" onClick={onClose}>Close</button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default FeesBilling;
